'use client';

import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { OPTION_LABEL_MAP, SCENE, TASK_SETTING_OPTION, TIMBRE_LIST, TYPE_KEY_MAP } from '@/app/constants';
import { useModelVersionText, useResolution } from '@/app/hooks';
import type { DisplayModelVersion, FormTabType, SettingsLanguage, TaskInfo } from '@/app/types';
import { cn, formatAspectRatio, isExtendTask, isTaskSettingVisible } from '@/app/utils';
import { getOneClickMvTaskScene } from '@/app/utils/client';

const ONE_CLICK_MV_SCENE_LABEL_KEYS = {
  sing: 'sing',
  sing_act: 'sing_act',
  dance: 'dance',
  performance: 'performance',
} as const;

const ONE_CLICK_MV_MODE_SCENE_MAP = {
  sing: 'sing',
  sing_perform: 'sing_act',
  dance: 'dance',
  perform: 'performance',
} as const;

interface TaskSettingTagsProps {
  taskId?: string;
  taskType: TaskInfo['type'];
  settings?: TaskInfo['settings'];
  sceneName?: string;
  duration?: number;
  showTaskType?: boolean;
  maxVisibleCount?: number;
  isHoverShowAll?: boolean; // 溢出时 hover 时展示所有 settings
  upscaleSourceResolution?: string;
  upscaleTargetResolution?: string;
  className?: string;
  tagClassName?: string;
}

export const TaskSettingTags = ({
  taskId,
  taskType,
  settings,
  sceneName,
  duration,
  showTaskType = true,
  maxVisibleCount,
  isHoverShowAll = false,
  upscaleSourceResolution,
  upscaleTargetResolution,
  className,
  tagClassName,
}: TaskSettingTagsProps) => {
  const t = useTranslations();
  const { getModelVersionText } = useModelVersionText();
  const { resolutionMap } = useResolution();

  const allTags = useMemo(() => {
    if (!settings) return [];

    const tags: string[] = [];
    const modelVersion = settings.model_version as DisplayModelVersion | undefined;
    const displayDuration = duration ?? settings.duration;

    const addTag = (tag?: string) => {
      if (tag) tags.push(tag);
    };

    const addOptionLabelTag = (value?: string) => {
      if (!value || value === 'unspecified') return;
      const labelKey = OPTION_LABEL_MAP[value];
      if (!labelKey) return;
      addTag(t(`create.createForm.settings.${labelKey}.text`));
    };

    const addModelVersionTag = () => {
      if (!modelVersion) return;
      addTag(
        getModelVersionText({
          modelVersion,
          transition: settings.transition,
        }),
      );
    };

    const addDurationTag = () => {
      if (!displayDuration) return;
      addTag(
        displayDuration === -1
          ? t('create.createForm.settings.duration.option.autoTag')
          : t('create.createForm.settings.duration.option.text', { time: displayDuration }),
      );
    };

    const addResolutionTag = () => {
      if (upscaleSourceResolution && upscaleTargetResolution) {
        addTag(`SR: ${upscaleSourceResolution} → ${upscaleTargetResolution}`);
      } else if (settings.resolution && resolutionMap[settings.resolution as keyof typeof resolutionMap]) {
        addTag(resolutionMap[settings.resolution as keyof typeof resolutionMap]);
      }
    };

    const addVoiceLanguageTag = (value?: SettingsLanguage) => {
      if (!value) return;

      addTag(t(`tools.languageOptions.${value}`));
    };

    const addRemakeExtraTags = () => {
      settings.retain_original_audio && addTag(t('tools.videoRemake.settings.withAudio'));
    };

    const addMotionControlExtraTags = () => {
      addTag(
        `${t('tools.motionControl.settings.useVideoBackground')}: ${
          settings.use_video_background
            ? t('tools.motionControl.settings.optionOn')
            : t('tools.motionControl.settings.optionOff')
        }`,
      );
    };

    const addOneClickExtraTags = () => {
      addTag(t(`tools.oneClickVideoProduction.scenario.${settings.biz_scene as 'general' | 'ecommerce_ad'}.label`));
      if (settings.biz_scene === 'ecommerce_ad') {
        settings.voice_lang && addVoiceLanguageTag(settings.voice_lang as SettingsLanguage);
      }
    };

    if (showTaskType) {
      addTag(t(`create.${TYPE_KEY_MAP[taskType]}`));
    }

    if (taskType === SCENE) {
      addTag(sceneName);
      addTag(formatAspectRatio(settings.aspect_ratio, t));
      return Array.from(new Set(tags));
    }

    if (taskType === 'text2audio') {
      addDurationTag();
      return Array.from(new Set(tags));
    }

    if (taskType === 'lip_sync') {
      addModelVersionTag();
      if (settings.voice_id) {
        const voiceName = TIMBRE_LIST.find((item) => item.id === String(settings.voice_id))?.name;
        if (voiceName) {
          addTag(voiceName);
        }
      }

      const rawSpeed = settings.speed;
      if (rawSpeed) {
        addTag(t('tools.lipSync.settings.taskTagSpeed', { speed: `${rawSpeed.toFixed(1)}x` }));
      }
      return Array.from(new Set(tags));
    }

    addModelVersionTag();

    if (isExtendTask(taskType)) {
      addDurationTag();
      addResolutionTag();
      addOptionLabelTag(settings.codec);
      return Array.from(new Set(tags));
    }

    if (modelVersion && isTaskSettingVisible(taskType, modelVersion, TASK_SETTING_OPTION.Style)) {
      addOptionLabelTag(settings.style);
    }

    if (
      modelVersion &&
      isTaskSettingVisible(taskType, modelVersion, [
        TASK_SETTING_OPTION.StylePreference,
        TASK_SETTING_OPTION.CreationMode,
      ])
    ) {
      addOptionLabelTag(settings.transition);
    }

    if (
      modelVersion &&
      isTaskSettingVisible(taskType, modelVersion, [TASK_SETTING_OPTION.Codec, TASK_SETTING_OPTION.ResolutionAndCodec])
    ) {
      addOptionLabelTag(settings.codec);
    }

    addDurationTag();

    if (taskType === 'one_click') {
      addOneClickExtraTags();
    }

    // 兼容 1.0 模型历史展示逻辑：展示 model(speed/standard)，不展示分辨率/宽高比/编码等新参数
    if (settings.model_version === '1.0') {
      addOptionLabelTag(settings.model);
      return Array.from(new Set(tags));
    }
    addResolutionTag();

    if (taskType === 'remake') {
      addRemakeExtraTags();
    }

    if (taskType === 'motion_control') {
      addMotionControlExtraTags();
    }

    if (
      modelVersion &&
      isTaskSettingVisible(taskType, modelVersion, [
        TASK_SETTING_OPTION.MovementAmplitude,
        TASK_SETTING_OPTION.MovementAmplitudeAndAspectRatio,
      ])
    ) {
      addOptionLabelTag(settings.movement_amplitude);
    }

    if (modelVersion && isTaskSettingVisible(taskType, modelVersion, TASK_SETTING_OPTION.AspectRatio)) {
      addTag(
        formatAspectRatio(settings.aspect_ratio, t, {
          taskType: taskType as Exclude<FormTabType, 'scene'>,
          modelVersion,
        }),
      );
    } else if (taskType === 'one_click' || taskType === 'remake' || taskType === 'music_video') {
      addTag(formatAspectRatio(settings.aspect_ratio, t));
    }

    if (taskType === 'music_video') {
      const musicVideoMode = (settings as TaskInfo['settings'] & { music_video?: { mode?: string } }).music_video?.mode;
      const markerMatch = sceneName?.match(/^one_click_mv:(v1|v2)(?::(sing|sing_act|dance|performance))?$/);
      const markerVersion = markerMatch?.[1];
      const markerScene = markerMatch?.[2] as keyof typeof ONE_CLICK_MV_SCENE_LABEL_KEYS | undefined;
      const isV2 = markerVersion === 'v2' || (!markerVersion && !!musicVideoMode && musicVideoMode !== 'unspecified');
      const modeScene = musicVideoMode
        ? ONE_CLICK_MV_MODE_SCENE_MAP[musicVideoMode as keyof typeof ONE_CLICK_MV_MODE_SCENE_MAP]
        : undefined;
      const storedScene = getOneClickMvTaskScene(taskId);
      const displayScene = markerScene || modeScene || storedScene;
      const isStoredV2 = !!storedScene;

      if ((isV2 || isStoredV2) && displayScene) {
        addTag(t(`tools.oneClickMv.scene.${ONE_CLICK_MV_SCENE_LABEL_KEYS[displayScene]}.label`));
      }
    }

    return Array.from(new Set(tags));
  }, [
    settings,
    showTaskType,
    taskType,
    taskId,
    sceneName,
    getModelVersionText,
    duration,
    upscaleSourceResolution,
    upscaleTargetResolution,
    resolutionMap,
  ]);

  const visibleTags = useMemo(() => {
    if (!maxVisibleCount || allTags.length <= maxVisibleCount) return allTags;

    if (maxVisibleCount >= 2) {
      return [...allTags.slice(0, maxVisibleCount - 1)];
    }

    return allTags.slice(0, maxVisibleCount);
  }, [allTags, maxVisibleCount]);

  const isLastItemOverflow = maxVisibleCount != null && maxVisibleCount >= 2 && allTags.length > maxVisibleCount;

  if (!visibleTags.length) return null;

  return (
    <CustomTooltip
      content={
        isHoverShowAll &&
        isLastItemOverflow && (
          <div className="flex flex-wrap items-center gap-y-0.5 text-sm text-system-white">
            {allTags.map((tag, i) => (
              <span key={`${taskType}-overflow-tag-${i}-${tag}`} className="inline-flex items-center">
                {i > 0 && <span className="px-2 text-system-white24 text-xs">|</span>}
                {tag}
              </span>
            ))}
          </div>
        )
      }
      disableHoverableContent={true}
      align="center"
    >
      <div className={cn('flex flex-wrap items-center', className)}>
        {visibleTags.map((tag, index) => {
          return (
            <span
              key={`${taskType}-setting-tag-${index}-${tag}`}
              className={cn('inline-flex items-center text-sm text-system-white32', tagClassName)}
            >
              {index > 0 && <span className="px-2 text-system-white08 text-xs">|</span>}
              {tag}
              {isLastItemOverflow && index === visibleTags.length - 1 && (
                <BaseLineExclamation className="ml-2 shrink-0 text-xs" />
              )}
            </span>
          );
        })}
      </div>
    </CustomTooltip>
  );
};
